import React from 'react'
import '../styles/generic-table.css'
import {
  chevronBackward,
  chevronForward,
  firstPageIcon,
  lastPageIcon,
} from '../../assets'

const defaultCellRenderer = value =>
  value === null || value === undefined ? '' : String(value)

const SkeletonRow = ({columns, rowIndex}) => (
  <tr key={`skeleton-${rowIndex}`}>
    {columns.map(column => (
      <td
        key={column.key}
        style={{
          padding: '24px',
          borderBottom: '1px solid #f0f0f0',
        }}
      >
        <div
          style={{
            height: '25px',
            background:
              'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
            backgroundSize: '200% 100%',
            animation: 'skeleton-loading 1.5s infinite',
            borderRadius: '4px',
            width: column.key === 'actions' ? '80px' : '100%',
          }}
        />
      </td>
    ))}
  </tr>
)

const SkeletonTable = ({columns, skeletonRows = 5}) => (
  <tbody>
    {Array.from({length: skeletonRows}, (_, index) => (
      <SkeletonRow key={index} columns={columns} rowIndex={index} />
    ))}
  </tbody>
)

const TableHeader = ({columns}) => (
  <thead>
    <tr>
      {columns.map(column => (
        <th
          key={column.key}
          style={{
            textAlign: 'left',
            paddingInline: '12px',
            paddingBlock: '12px',
            borderBottom: '1px solid #E3E6E8',
            background: '#F8F9FB',
            fontFamily: 'Open Sans',
            fontWeight: 400,
            fontSize: '13px',
            lineHeight: '18px',
            letterSpacing: '0px',
            color: '#808080',
            whiteSpace: 'normal',
            overflow: 'visible',
            textOverflow: 'clip',
            minWidth: column.key === 'actions' ? '120px' : '70px',
            maxWidth: column.key === 'actions' ? '150px' : 'none',
          }}
        >
          {column.label}
        </th>
      ))}
    </tr>
  </thead>
)

const TableBody = ({
  columns,
  rows,
  emptyMessage,
  isLoading,
  loadingMessage,
  skeletonRows,
}) => {
  if (isLoading) {
    return <SkeletonTable columns={columns} skeletonRows={skeletonRows} />
  }

  if (!rows || rows.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns.length} style={{padding: '16px', color: '#666'}}>
            {emptyMessage || 'No data to display.'}
          </td>
        </tr>
      </tbody>
    )
  }

  return (
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr key={row.id || rowIndex}>
          {columns.map(column => {
            const value = column.accessor
              ? column.accessor(row)
              : row[column.key]
            const content = column.render
              ? column.render(value, row)
              : defaultCellRenderer(value)
            return (
              <td
                key={column.key}
                style={{
                  padding: '12px',
                  borderBottom: '1px solid #f0f0f0',
                  fontFamily: 'Montserrat',
                  fontWeight: '600',
                  fontStyle: 'SemiBold',
                  fontSize: '13px',
                  lineHeight: '18px',
                  letterSpacing: '0px',
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  wordBreak: 'break-word',
                  minWidth: column.key === 'actions' ? '120px' : '70px',
                  maxWidth: column.key === 'actions' ? '150px' : 'none',
                }}
              >
                {content}
              </td>
            )
          })}
        </tr>
      ))}
    </tbody>
  )
}

const Pagination = ({page, totalPages, onPageChange, hasPrev, hasNext}) => {
  if (totalPages <= 1) return null

  // Generate page numbers to display (always show 5 numbers)
  const getPageNumbers = () => {
    const pages = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      let start = Math.max(1, page - 2)
      let end = Math.min(totalPages, page + 2)

      if (page <= 3) {
        start = 1
        end = Math.min(5, totalPages)
      } else if (page >= totalPages - 2) {
        start = Math.max(1, totalPages - 4)
        end = totalPages
      }

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
    }

    return pages
  }

  const pageNumbers = getPageNumbers()

  return (
    <div
      style={{
        display: 'flex',
        gap: 4,
        alignItems: 'center',
        justifyContent: 'center',
        paddingTop: 12,
        fontFamily: 'Open Sans',
        fontWeight: '700',
        fontSize: '12px',
        leadingTrim: 'NONE',
        lineHeight: '18px',
        letterSpacing: '0px',
        textAlign: 'center',
      }}
    >
      {/* First page button */}
      <button
        disabled={page === 1}
        onClick={() => onPageChange(1)}
        className="pagination-btn"
        style={{
          cursor: page === 1 ? 'not-allowed' : 'pointer',
          background: page === 1 ? '#f5f5f5' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={firstPageIcon} alt="First" />
      </button>

      {/* Previous page button */}
      <button
        disabled={!hasPrev}
        onClick={() => onPageChange(page - 1)}
        className="pagination-btn"
        style={{
          cursor: page === 1 ? 'not-allowed' : 'pointer',
          background: page === 1 ? '#f5f5f5' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={chevronBackward} alt="Previous" />
      </button>

      {/* Page number buttons */}
      {pageNumbers.map(pageNum => (
        <button
          key={pageNum}
          onClick={() => onPageChange(pageNum)}
          className={`pagination-btn ${pageNum === page ? 'active' : ''}`}
        >
          {pageNum}
        </button>
      ))}

      {/* Next page button */}
      <button
        disabled={!hasNext}
        onClick={() => onPageChange(page + 1)}
        className="pagination-btn"
        style={{
          cursor: page === totalPages ? 'not-allowed' : 'pointer',
          background: page === totalPages ? '#f5f5f5' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={chevronForward} alt="Next" />
      </button>

      {/* Last page button */}
      <button
        disabled={page === totalPages}
        onClick={() => onPageChange(totalPages)}
        className="pagination-btn"
        style={{
          cursor: page === totalPages ? 'not-allowed' : 'pointer',
          background: page === totalPages ? '#f5f5f5' : '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <img src={lastPageIcon} alt="Last" />
      </button>
    </div>
  )
}

const GenericTable = ({
  columns,
  rows,
  emptyMessage,
  pagination,
  isLoading,
  loadingMessage,
  skeletonRows = 5,
}) => {
  return (
    <div>
      <style>
        {`
          @keyframes skeleton-loading {
            0% {
              background-position: -200% 0;
            }
            100% {
              background-position: 200% 0;
            }
          }
        `}
      </style>
      <div style={{width: '100%', overflowX: 'auto', overflowY: 'visible'}}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: '#fff',
            tableLayout: 'auto',
          }}
        >
          <TableHeader columns={columns} />
          <TableBody
            columns={columns}
            rows={rows}
            emptyMessage={emptyMessage}
            isLoading={isLoading}
            loadingMessage={loadingMessage}
            skeletonRows={skeletonRows}
          />
        </table>
      </div>
      {pagination ? (
        <Pagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          hasPrev={pagination.hasPrev}
          hasNext={pagination.hasNext}
          onPageChange={pagination.onPageChange}
        />
      ) : null}
    </div>
  )
}

export default GenericTable
